import { useContext, useEffect, useState } from "preact/hooks";
import { route } from "preact-router";
import { Text } from "preact-i18n";

import { Configs } from "../../helpers/context";
import { getPrimaryButtonStyle } from "./helpers";
import { formatIntegerCurrencyString } from "../../helpers/formatters";
import { EMIT_LOAD_PRODUCT_PAYLINK } from "../../helpers/event-definitions";
import { createLineItemSignal } from "../../signals/lineItem";

import ProductPayLinkRow from "./ProductPayLinkRow";
import ProductPayLinkDetail from "./ProductPayLinkDetail";
import { getSelectedAddons } from "./ProductPayLinkModifier";

import "../../style/ProductPayLinkPage.css";

// component for multi-product pay link flow (type "M"):
// list products -> customize one -> add to cart -> checkout
const ProductPayLinkPage = ({ payLink, order, products, c2Analytics }) => {
  const configsContext = useContext(Configs);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    if (c2Analytics?.trackCustomEvent) {
      c2Analytics.trackCustomEvent(EMIT_LOAD_PRODUCT_PAYLINK);
    }
  }, []);

  if (!order || !products) {
    return null;
  }

  const lineItems = order.lineItems.value;
  const cartCount = lineItems.reduce((total, item) => total + item.quantity.value, 0);
  const cartTotal = lineItems.reduce(
    (total, item) => total + item.lineItem.value.unitAmount.value * item.quantity.value,
    0,
  );

  function addToCart({ product, sku, quantity, modifierValues }) {
    const currencyCode = sku.currencyCode || payLink.currency;
    const selectedAddons = getSelectedAddons(product.modifiers, modifierValues, currencyCode);
    const addonsKey = JSON.stringify(selectedAddons);

    // merge into an existing line item for the same sku and modifiers
    const existing = lineItems.find(
      (item) =>
        item.skuId.value === sku.id &&
        JSON.stringify(item.lineItem.value.details.selectedAddons) === addonsKey,
    );

    if (existing) {
      existing.quantity.value = existing.quantity.value + quantity;
    } else {
      order.lineItems.value = [
        ...lineItems,
        createLineItemSignal(
          {
            productId: product.id,
            skuId: sku.id,
            name:
              product.skus.length > 1 && (sku.displayLabel || sku.label)
                ? `${product.name} - ${sku.displayLabel || sku.label}`
                : product.name,
            quantity,
            unitAmount: { value: sku.price, currencyCode: sku.currencyCode },
            // unitAmount is the base sku price; addon cost adjustments get added on top
            unitAmountIncludesAdjustments: false,
            details: {
              selectedAddons,
              sku: sku.code || sku.id,
              productAssetUrl: product.imageUrl,
              unitOfMeasure: "EACH",
            },
          },
          { currency: payLink.currency },
        ),
      ];
    }

    setSelectedProduct(null);
  }

  function continueToPayment() {
    window?.parent?.postMessage(
      { sender: "pay-buttons-js", action: "setCheckoutContinue", value: true },
      "*",
    );

    route("/checkout");
  }

  const cartBadge = (
    <div className="ppl-cart" role={cartCount ? "button" : undefined} onClick={cartCount ? continueToPayment : undefined}>
      <svg className="ppl-cart-icon" aria-label="cart" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
        <path d="M2 3h3l2.4 11.2a1 1 0 0 0 1 .8h9.2a1 1 0 0 0 1-.8L20.5 7H6" />
        <circle cx="9" cy="19.5" r="1.25" />
        <circle cx="17" cy="19.5" r="1.25" />
      </svg>
      {cartCount ? <span className="ppl-cart-count">{cartCount}</span> : null}
    </div>
  );

  if (selectedProduct) {
    return (
      <div className="ppl-wrapper">
        {cartBadge}
        <ProductPayLinkDetail
          product={selectedProduct}
          onBack={() => setSelectedProduct(null)}
          onAddToCart={addToCart}
        />
      </div>
    );
  }

  const checkoutButtonStyle = getPrimaryButtonStyle(configsContext?.configs?.ecommerceCheckoutButtonColor);

  return (
    <div className="ppl-wrapper">
      {cartBadge}
      <div className="container ppl-page-section">
        <h1 className="ppl-title">{payLink.title}</h1>
        <p className="ppl-subtitle">
          {payLink.description || (
            <Text id="PRODUCT_PAYLINK_SUBTITLE">
              Choose a product, customize it, then add it to your cart.
            </Text>
          )}
        </p>

        {products.length ? (
          products.map((product) => (
            <ProductPayLinkRow
              product={product}
              quantityInCart={lineItems
                .filter((item) => item.productId.value === product.id)
                .reduce((total, item) => total + item.quantity.value, 0)}
              onAdd={setSelectedProduct}
            />
          ))
        ) : (
          <p className="ppl-empty">
            <Text id="PRODUCT_PAYLINK_EMPTY">No products are available right now.</Text>
          </p>
        )}

        {cartCount ? (
          <button
            type="button"
            className="ppl-primary-button"
            style={checkoutButtonStyle}
            onClick={continueToPayment}
          >
            <Text id="ITEM_LAYOUT_CHECKOUT">Checkout</Text> ({cartCount}) ·{" "}
            {formatIntegerCurrencyString(
              cartTotal,
              payLink.currency,
              configsContext?.configs?.ecommerceLocaleLanguage,
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default ProductPayLinkPage;
